import { createTheme, ThemeOptions, ThemeProvider } from '@mui/material/styles';
import cn from 'classnames';
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableProps,
  MUIDataTableState,
} from 'mui-datatables';
import React, { memo, useMemo } from 'react';
import { COLOR_CODE } from 'src/appConfig/constants';
import { LoadingCommon, View } from 'src/components/common';
import CustomFooterRender from './customFooterRender';
import CustomSearchRender from './customSearchRender';
import './styles.scss';

const TableBasic: React.FC<Props> = ({
  isLoading,
  containerClassName,
  onTableChange,
  currentPage,
  options,
  total,
  emptyComponent = 'No data',
  noColorStyle = false,
  data,
  grayHeader = false,
  isShowCountSelected = false,
  isEditMode = false,
  customRowsPerPageOptions = [],
  loadingComponent = null,
  forceUpdate,
  rowSelectedBackgroundColor,
  isFilterColumn = false,
  ...props
}) => {
  const tableOptions: MUIDataTableOptions = {
    serverSide: true,
    searchOpen: false,
    search: true,
    download: false,
    filter: true,
    print: false,
    viewColumns: false,
    selectableRowsHeader: false,
    selectableRows: 'none',
    textLabels: {
      body: {
        noMatch: isLoading ? (
          loadingComponent ? (
            loadingComponent
          ) : (
            <LoadingCommon />
          )
        ) : (
          emptyComponent
        ),
      },
    },
    jumpToPage: false,
    rowHover: true,
    onTableChange,
    customSearchRender: (searchText: string, handleSearch: (text: string) => void) => {
      return (
        <CustomSearchRender
          searchText={searchText}
          onSearch={handleSearch}
          placeholder={options.searchPlaceholder}
        />
      );
    },
    customFooter: (
      count: number,
      page: number,
      rowsPerPage: number,
      changeRowsPerPage: (page: string | number) => void,
      changePage: (newPage: number) => void
    ) => {
      return (
        <CustomFooterRender
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          changeRowsPerPage={changeRowsPerPage}
          customRowsPerPageOptions={customRowsPerPageOptions}
          changePage={changePage}
          theme={getMuiTheme()}
        />
      );
    },

    ...options,
  };

  const hasRowClickAction = !!options?.onRowClick;
  const getMuiTheme = () =>
    createTheme({
      primary: {
        main: COLOR_CODE.PRIMARY,
        dark: COLOR_CODE.PRIMARY_DARK,
        light: COLOR_CODE.PRIMARY_LIGHT,
      },
      secondary: {
        main: COLOR_CODE.SECONDARY,
      },
      typography: {
        fontFamily: ['Poppins', 'sans-serif'].join(','),
        button: {
          textTransform: 'none',
        },
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            elevation4: {
              boxShadow: 'none',
            },
            root: {
              boxShadow: isShowCountSelected && 'none',
            },
          },
        },
        MuiTableHead: {
          styleOverrides: {
            root: {
              height: 44,
              borderRadius: '8px',
              backgroundColor: COLOR_CODE.NEWCOLOR,
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            hover: {
              cursor: hasRowClickAction ? 'pointer' : 'default',
            },
            root: {
              height: '44px',
              '&:nth-of-type(even)': {
                backgroundColor: noColorStyle ? '' : COLOR_CODE.PRIMARY_50,
              },
            },
          },
        },
        MUIDataTableToolbarSelect: {
          styleOverrides: {
            root: {
              display: isShowCountSelected ? 'true' : 'none',
            },
            title: {
              display: 'none',
            },
          },
        },

        MUIDataTableToolbar: {
          styleOverrides: {
            root: {
              display: 'contents',
            },
            left: {
              display: 'none',
            },
          },
        },
        MUIDataTableBodyRow: {
          styleOverrides: {
            root: {
              borderBottom: noColorStyle ? '1px solid #DEE1E5' : '',
              '&.mui-row-selected': {
                backgroundColor: rowSelectedBackgroundColor,
              },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              whiteSpace: 'pre',
              padding: '4px 16px',
            },
            body: {
              cursor: hasRowClickAction ? 'pointer' : 'default',
              '&.MuiTableCell-root': {
                borderBottom: noColorStyle ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                verticalAlign: isEditMode ? 'initial' : 'inherit',
              },
            },
            head: {
              '&.MuiTableCell-root': {
                backgroundColor: grayHeader ? '#DEE1E5' : COLOR_CODE.NEWCOLOR,
                color: grayHeader ? 'black' : '#2D2F31',
                fontWeight: 'bold',
                padding: '0 16px',
                fontSize: '14px',
                zIndex: 1,
                borderBottom: '1px solid #DEE1E5',
              },
              '&.MuiTableCell-root span button': {
                color: '#2D2F31',
                fontWeight: 'bold',
              },
              '&.MuiTableCell-root span button div, &.MuiTableCell-root span button div span svg': {
                color: '#2D2F31 !important',
                fontWeight: 'bold',
                fontSize: '14px',
              },
            },
            footer: {
              '&.MuiTableCell-root': {
                borderBottom: 'none',
                padding: '6px 0',
              },
            },
          },
        },
        MuiTableBody: {
          styleOverrides: {
            root: {
              opacity: isLoading ? 0.3 : 1,
            },
          },
        },

        MuiPaginationItem: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                backgroundColor: COLOR_CODE.SECONDARY,
                color: COLOR_CODE.WHITE,
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              // Hide Chip filter
              '&.MuiChip-filled.MuiChip-colorDefault.MuiChip-deletable': {
                display: 'none !important',
              },
            },
          },
        },
      },
    } as ThemeOptions);

  const muiDataTable = useMemo(
    () => {
      return (
        <MUIDataTable
          options={tableOptions}
          data={loadingComponent && isLoading ? [] : data}
          {...props}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, forceUpdate, isEditMode]
  );

  // More info: https://github.com/gregnb/mui-datatables
  return (
    <View className={cn('cmp-table-basic', containerClassName)} flexGrow={1}>
      <ThemeProvider theme={getMuiTheme()}>
        {muiDataTable}
        {/* <MUIDataTable options={tableOptions} {...props} /> */}
      </ThemeProvider>
    </View>
  );
};

type Props = MUIDataTableProps & {
  containerClassName?: string;
  currentPage?: number;
  total?: number;
  onTableChange: (action: string, tableState: MUIDataTableState) => void;
  isLoading?: boolean;
  noColorStyle?: boolean;
  emptyComponent?: React.ReactNode;
  isShowCountSelected?: boolean;
  grayHeader?: boolean;
  isEditMode?: boolean;
  customRowsPerPageOptions?: number[];
  loadingComponent?: React.ReactNode;
  forceUpdate?: {};
  rowSelectedBackgroundColor?: string;
  isFilterColumn?: boolean;
};

export default memo(TableBasic);
